// "On Scouts now": what every app is on, and since when. Apps with updates on
// the page can be chosen, one or several, to show only their updates; one
// without (Compass, which Scouts never report) is shown, not offered.

import { CheckIcon } from '@heroicons/react/20/solid';
import { cx } from '../../../../components/admin/console';
import { AppIcon } from './AppIcon';
import { BUSY_NAME_ROOM, BUSY_TEXT_ROOM, BusyMark } from './parts';
import { nzShortDay, type LiveApp } from '../data/changelog';

/** "15 Oct": the weekday adds nothing on a tile. */
const dayMonth = (iso: string) => nzShortDay(iso).replace(/^\w+ /, '');

export function LiveApps({ apps, next, chosen, filterable, onPick, busiest = null }: {
  apps: LiveApp[];
  /** App → the version on its way, for apps with something not sent yet. */
  next: Record<string, string>;
  /** The apps chosen; empty shows every app. */
  chosen: Set<string>;
  filterable: Set<string>;
  onPick: (app: string) => void;
  /** The app with the most commits lately: its tile gets a small flame. */
  busiest?: string | null;
}) {
  if (!apps.length) return null;
  return (
    <section aria-labelledby="live-apps-h" className="rounded-2xl border border-slate-700 bg-gradient-to-b from-slate-800/55 to-slate-800/30 px-4 pb-4 pt-4 sm:px-5 sm:pb-5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 id="live-apps-h" className="flex items-center gap-2 text-base font-semibold text-white">
          <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_0_4px_rgba(52,211,153,0.15)]" aria-hidden="true" />
          On Scouts now
        </h2>
        <span className="text-sm text-slate-400">
          <span className="sm:hidden">Choose one or more apps to filter</span>
          <span className="hidden sm:inline">Choose one or more apps to see only their updates</span>
        </span>
      </div>
      <ul className="mt-3.5 grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-2.5 lg:grid-cols-4">
        {apps.map((a) => {
          const since = a.since
            ? `Since ${dayMonth(a.since)}`
            : <><span className="sm:hidden">Date not reported</span><span className="hidden sm:inline">Date not reported by Scouts</span></>;
          const body = (
            <>
              <span className="sm:hidden"><AppIcon app={a.app} size={28} /></span>
              <span className="hidden sm:inline-flex"><AppIcon app={a.app} size={40} /></span>
              <span className={cx('flex w-full min-w-0 flex-col', busiest === a.app && BUSY_TEXT_ROOM)}>
                <span className={cx('truncate text-[15px] font-semibold text-white', busiest === a.app && BUSY_NAME_ROOM)}>{a.name}</span>
                <span className="mt-0.5 font-mono text-sm text-slate-200">{a.version}</span>
                <span className="text-[13px] leading-snug text-slate-400">{since}</span>
                {next[a.app] && <span className="mt-0.5 text-[13px] font-medium leading-snug text-amber-300">Next: {next[a.app]}</span>}
              </span>
            </>
          );
          const selected = chosen.has(a.app);
          return (
            <li key={a.app} className="min-w-0">
              {filterable.has(a.app) ? (
                <button
                  type="button"
                  onClick={() => onPick(a.app)}
                  aria-pressed={selected}
                  className={cx(
                    'relative flex h-full w-full min-w-0 flex-col items-start gap-2 rounded-xl border p-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 sm:flex-row sm:items-center sm:gap-3',
                    selected ? 'border-cyan-400/60 bg-cyan-400/[0.08]' : 'border-slate-700 bg-slate-950/45 hover:border-slate-600 hover:bg-slate-800/90',
                  )}
                >
                  {body}
                  {busiest === a.app && <BusyMark />}
                  {selected && (
                    <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-cyan-400 text-slate-950" aria-hidden="true">
                      <CheckIcon className="h-3.5 w-3.5" />
                    </span>
                  )}
                </button>
              ) : (
                <div className="relative flex h-full min-w-0 flex-col items-start gap-2 rounded-xl border border-dashed border-slate-700 bg-slate-950/30 p-3 sm:flex-row sm:items-center sm:gap-3" title="No updates for this app are listed below">
                  {body}
                  {busiest === a.app && <BusyMark />}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
